import { useState, useMemo } from 'react';
import { Card, Badge, SlidePanel, Modal, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { MpBookingDetailContent, type BookingDetail } from './MpBookingDetail';
import styles from './MpCalendar.module.css';

interface Booking {
  id: string;
  property_name: string;
  guest_name: string;
  ota: string;
  check_in: string;
  check_out: string;
  nights: number;
  amount: number;
  listing_id?: string;
  property_id?: string;
}

interface Listing {
  id: string;
  property_id: string;
  property_name: string;
  days_used: number;
  max_days: number;
  smart_lock_code?: string;
  wifi_ssid?: string;
  wifi_password?: string;
  address?: string;
}

interface Property {
  id: string;
  name: string;
  address: string;
}

const otaColor: Record<string, string> = { Airbnb: '#FF5A5F', STAY: '#2563EB', Booking: '#003580' };
const otaLabel: Record<string, string> = { Airbnb: 'Airbnb', STAY: 'STAY', Booking: 'Bking' };

export function MpCalendar() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [otaFilter, setOtaFilter] = useState('all');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    listing_id: '', property_id: '', guest_name: '', ota: 'Airbnb',
    check_in: '', check_out: '', nights: '1', amount: '',
  });

  const { data: bookings, loading, refetch } = useApi<Booking[]>(
    `/tenant/minpaku/bookings?year=${year}&month=${String(month).padStart(2, '0')}`
  );
  const { data: listings } = useApi<Listing[]>('/tenant/minpaku/listings');
  const { data: properties } = useApi<Property[]>('/tenant/properties');

  const daysInMonth = new Date(year, month, 0).getDate();

  const prevMonth = () => { if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1); };

  const filteredBookings = useMemo(() => {
    if (!bookings) return [];
    let result = bookings;
    if (otaFilter !== 'all') result = result.filter(b => b.ota === otaFilter);
    if (propertyFilter !== 'all') result = result.filter(b => b.property_name === propertyFilter);
    return result;
  }, [bookings, otaFilter, propertyFilter]);

  // 全物件名（フィルター用）
  const allPropertyNames = useMemo(() => {
    if (!bookings) return [];
    return [...new Set(bookings.map(b => b.property_name))];
  }, [bookings]);

  const { propertyBookings, propertyNames } = useMemo(() => {
    const map = new Map<string, Booking[]>();
    filteredBookings.forEach((b) => {
      const key = b.property_name;
      if (!map.has(key)) map.set(key, []);
      const existing = map.get(key)!;
      if (!existing.find((e) => e.id === b.id)) existing.push(b);
    });
    return { propertyBookings: map, propertyNames: Array.from(map.keys()) };
  }, [filteredBookings]);

  const upcomingBookings = useMemo(() => {
    const seen = new Set<string>();
    const result: Booking[] = [];
    filteredBookings.forEach((b) => {
      const key = `${b.guest_name}|${b.check_in}|${b.property_name}`;
      if (!seen.has(key)) { seen.add(key); result.push(b); }
    });
    return result
      .filter((b) => new Date(b.check_in) >= new Date(now.getFullYear(), now.getMonth(), now.getDate()))
      .sort((a, b) => new Date(a.check_in).getTime() - new Date(b.check_in).getTime());
  }, [filteredBookings]);

  if (loading) return <div className={styles.page}>読み込み中...</div>;

  const gridCols = daysInMonth + 1;
  const isToday = (day: number) => year === now.getFullYear() && month === now.getMonth() + 1 && day === now.getDate();

  const getRelativeDay = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diff = Math.round((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'チェックイン当日';
    if (diff === 1) return '明日';
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // Convert Booking to BookingDetail using listing data from DB
  const toDetail = (b: Booking): BookingDetail => {
    const listing = (listings ?? []).find(l => l.property_id === b.property_id || l.id === b.listing_id);
    const prop = (properties ?? []).find(p => p.name === b.property_name);
    return {
      ...b,
      per_night: b.nights > 0 ? Math.round(b.amount / b.nights) : 0,
      days_used: listing?.days_used,
      max_days: listing?.max_days ?? 180,
      smart_lock_code: listing?.smart_lock_code,
      wifi_ssid: listing?.wifi_ssid,
      wifi_password: listing?.wifi_password,
      address: prop?.address ?? listing?.address,
    };
  };

  const handleCreateBooking = async () => {
    if (!form.listing_id || !form.guest_name || !form.check_in || !form.check_out) {
      toast('必須項目を入力してください', 'error'); return;
    }
    try {
      await api('/tenant/minpaku/bookings', {
        method: 'POST',
        body: JSON.stringify({
          listing_id: form.listing_id,
          property_id: form.property_id,
          guest_name: form.guest_name,
          ota: form.ota,
          check_in: form.check_in,
          check_out: form.check_out,
          nights: Number(form.nights),
          amount: Number(form.amount) || 0,
        }),
      });
      toast('予約を登録しました', 'success');
      setShowCreate(false);
      setForm({ listing_id: '', property_id: '', guest_name: '', ota: 'Airbnb', check_in: '', check_out: '', nights: '1', amount: '' });
      refetch();
    } catch (e) {
      toast(e instanceof Error ? e.message : '登録に失敗しました', 'error');
    }
  };

  const handleListingSelect = (listingId: string) => {
    const listing = (listings ?? []).find(l => l.id === listingId);
    setForm({ ...form, listing_id: listingId, property_id: listing?.property_id || '' });
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    try {
      await api(`/tenant/minpaku/bookings/${selectedBooking.id}`, { method: 'DELETE' });
      toast('予約をキャンセルしました', 'success');
      setSelectedBooking(null);
      refetch();
    } catch { toast('キャンセルに失敗しました', 'error'); }
  };

  return (
    <div className={styles.page}>
      <Card title={
        <div className={styles.header}>
          <button className={styles.navBtn} onClick={prevMonth}>← 前月</button>
          <span className={styles.monthLabel}>{year}年 {month}月</span>
          <button className={styles.navBtn} onClick={nextMonth}>次月 →</button>
          <select className={styles.propFilter} value={propertyFilter} onChange={e => setPropertyFilter(e.target.value)}>
            <option value="all">全物件</option>
            {allPropertyNames.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <div className={styles.filters}>
            {['all', 'Airbnb', 'STAY', 'Booking'].map(ota => (
              <button key={ota} className={`${styles.filterBtn} ${otaFilter === ota ? styles.filterActive : ''}`}
                style={ota !== 'all' && otaFilter === ota ? { background: otaColor[ota], color: '#fff', borderColor: otaColor[ota] } : {}}
                onClick={() => setOtaFilter(ota)}>{ota === 'all' ? '全OTA' : ota}</button>
            ))}
          </div>
          <button className={styles.addBtn} onClick={() => setShowCreate(true)}>＋ 予約登録</button>
        </div>
      }>
        <div className={styles.legend}>
          <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#FF5A5F' }} />Airbnb</span>
          <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#2563EB' }} />STAY</span>
          <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#003580' }} />Booking</span>
          <span className={styles.legendItem}><span className={styles.legendBlock} />ブロック（清掃準備）</span>
        </div>
        <div className={styles.ganttWrap}>
          <div className={styles.gantt} style={{ gridTemplateColumns: `140px repeat(${daysInMonth}, 1fr)` }}>
            <div className={styles.ganttCorner}>物件名</div>
            {Array.from({ length: daysInMonth }, (_, i) => {
              const d = new Date(year, month - 1, i + 1);
              const weekend = d.getDay() === 0 || d.getDay() === 6;
              return <div key={i} className={`${styles.ganttDayHeader} ${weekend ? styles.weekend : ''} ${isToday(i + 1) ? styles.today : ''}`}>{i + 1}</div>;
            })}
            {propertyNames.map((propName) => {
              const propBookings = propertyBookings.get(propName) ?? [];
              return (
                <div key={propName} className={styles.ganttPropertyRow}>
                  <div className={styles.ganttPropertyName}>{propName}</div>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const d = new Date(year, month - 1, day);
                    const weekend = d.getDay() === 0 || d.getDay() === 6;
                    const startingBooking = propBookings.find((b) => {
                      const ci = new Date(b.check_in);
                      return ci.getFullYear() === year && ci.getMonth() + 1 === month && ci.getDate() === day;
                    });
                    // 清掃ブロック: チェックアウト日に開始し、次の予約のチェックイン前日まで
                    const cleaningBlock = (() => {
                      const coBooking = propBookings.find(b => {
                        const co = new Date(b.check_out);
                        return co.getFullYear() === year && co.getMonth() + 1 === month && co.getDate() === day;
                      });
                      if (!coBooking) return null;
                      // 次の予約を探す
                      const coDate = new Date(coBooking.check_out);
                      const nextBooking = propBookings
                        .filter(b => new Date(b.check_in) >= coDate)
                        .sort((a, b) => new Date(a.check_in).getTime() - new Date(b.check_in).getTime())[0];
                      const nextCi = nextBooking ? new Date(nextBooking.check_in) : null;
                      const cleanEnd = nextCi && nextCi.getMonth() + 1 === month ? nextCi.getDate() : day + 1;
                      const span = cleanEnd - day;
                      if (span <= 0) return null;
                      return span;
                    })();
                    return (
                      <div key={day} className={`${styles.ganttCell} ${weekend ? styles.weekend : ''} ${isToday(day) ? styles.today : ''}`}>
                        {startingBooking && (() => {
                          const ci = new Date(startingBooking.check_in);
                          const co = new Date(startingBooking.check_out);
                          const startDay = ci.getMonth() + 1 === month ? ci.getDate() : 1;
                          const endDay = co.getMonth() + 1 === month ? Math.min(co.getDate(), daysInMonth) : daysInMonth;
                          const span = endDay - startDay;
                          if (span <= 0) return null;
                          return (
                            <div className={styles.bookingBar}
                              style={{ background: otaColor[startingBooking.ota] ?? 'var(--brand)', left: 0, width: `calc(${span * 100}%)` }}
                              onClick={() => setSelectedBooking(startingBooking)}>
                              <span className={styles.bookingBarLabel}>{startingBooking.guest_name}</span>
                            </div>
                          );
                        })()}
                        {!startingBooking && cleaningBlock && (
                          <div className={styles.cleaningBar} style={{ left: 0, width: `calc(${cleaningBlock * 100}%)` }}>
                            <span className={styles.cleaningBarLabel}>清掃準備</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {propertyNames.length === 0 && (
              <div className={styles.ganttPropertyName} style={{ color: 'var(--ink3)', gridColumn: `1 / span ${gridCols}`, textAlign: 'center', padding: 24 }}>予約データなし</div>
            )}
          </div>
        </div>
      </Card>

      <Card title="📋 今後7日間の予約（チェックイン順）">
        <div className={styles.bookingList}>
          {upcomingBookings.slice(0, 10).map((b) => (
            <div key={b.id} className={styles.bookingItem} onClick={() => setSelectedBooking(b)}>
              <div className={styles.bookingOtaBadge} style={{ background: otaColor[b.ota] ?? 'var(--brand)' }}>{otaLabel[b.ota] ?? b.ota}</div>
              <div className={styles.bookingBody}>
                <div className={styles.bookingGuest}>{b.guest_name} 様 ｜ {b.property_name}</div>
                <div className={styles.bookingDates}>
                  CI: {new Date(b.check_in).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}
                  {' → CO: '}{new Date(b.check_out).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}
                  {' ｜ '}{b.nights}泊
                </div>
              </div>
              <div className={styles.bookingRight}>
                <div className={styles.bookingAmount}>¥{b.amount.toLocaleString()}</div>
                <Badge variant={getRelativeDay(b.check_in) === 'チェックイン当日' ? 'amber' : 'blue'}>{getRelativeDay(b.check_in)}</Badge>
              </div>
            </div>
          ))}
          {upcomingBookings.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)' }}>今後の予約なし</div>}
        </div>
      </Card>

      <SlidePanel open={!!selectedBooking} onClose={() => setSelectedBooking(null)} title={selectedBooking ? `予約詳細 — ${selectedBooking.guest_name} 様` : ''} width={560}>
        {selectedBooking && <MpBookingDetailContent booking={toDetail(selectedBooking)} onMessage={() => {}} onCancel={handleCancelBooking} />}
      </SlidePanel>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="予約登録" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.navBtn} onClick={() => setShowCreate(false)}>キャンセル</button>
          <button className={styles.addBtn} onClick={handleCreateBooking}>登録</button>
        </div>
      }>
        <div className={styles.createForm}>
          <div className={styles.formGroup}><label className={styles.formLabel}>物件 *</label>
            <select className={styles.formInput} value={form.listing_id} onChange={e => handleListingSelect(e.target.value)}>
              <option value="">選択してください</option>
              {(listings ?? []).map(l => <option key={l.id} value={l.id}>{l.property_name}</option>)}
            </select>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>ゲスト名 *</label>
            <input className={styles.formInput} value={form.guest_name} onChange={e => setForm({ ...form, guest_name: e.target.value })} placeholder="John Smith" />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>OTA</label>
              <select className={styles.formInput} value={form.ota} onChange={e => setForm({ ...form, ota: e.target.value })}>
                <option value="Airbnb">Airbnb</option><option value="Booking">Booking.com</option><option value="STAY">STAY JAPAN</option><option value="direct">直接予約</option>
              </select>
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>宿泊数</label>
              <input className={styles.formInput} type="number" value={form.nights} onChange={e => setForm({ ...form, nights: e.target.value })} />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>チェックイン *</label>
              <input className={styles.formInput} type="date" value={form.check_in} onChange={e => setForm({ ...form, check_in: e.target.value })} />
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>チェックアウト *</label>
              <input className={styles.formInput} type="date" value={form.check_out} onChange={e => setForm({ ...form, check_out: e.target.value })} />
            </div>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>金額（円）</label>
            <input className={styles.formInput} type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="25000" />
          </div>
        </div>
      </Modal>
    </div>
  );
}
